// YERİ: src/app/domains/site/sections/map.section.ts
// Harita — Google Maps (adres ile) veya hazır embed URL (Google/OSM/diğer). Config:
// { title, provider, query (adres), embedUrl, height }. iframe src sanitize edilir.

import { Component, computed, inject, input } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SiteConfig, cfg } from './section.model';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'sec-map',
  standalone: true,
  imports: [TranslocoPipe],
  template: `
    <section id="map" class="bg-white py-16 dark:bg-neutral-950">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        @if (title()) { <h2 class="mb-6 text-center font-[family-name:var(--site-head-font)] text-2xl font-bold tracking-tight text-neutral-900 dark:text-white">{{ title() }}</h2> }
        @if (src(); as s) {
          <div class="overflow-hidden rounded-2xl border border-neutral-200 shadow-sm dark:border-neutral-800">
            <iframe [src]="s" class="w-full border-0" [style.height.px]="heightPx()" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>
          </div>
        } @else {
          <div class="flex items-center justify-center rounded-2xl border border-dashed border-neutral-300 text-sm text-neutral-400 dark:border-neutral-700" [style.height.px]="heightPx()">
            {{ 'siteEditor.noMap' | transloco }}
          </div>
        }
      </div>
    </section>
  `,
})
export class MapSection {
  private sanitizer = inject(DomSanitizer);
  config = input<SiteConfig>({});
  protected title = computed(() => cfg(this.config(), 'title', ''));
  private provider = computed(() => cfg<string>(this.config(), 'provider', 'google'));
  private query = computed(() => cfg<string>(this.config(), 'query', ''));
  private embedUrl = computed(() => cfg<string>(this.config(), 'embedUrl', ''));
  protected heightPx = computed(() => {
    const h = cfg<string>(this.config(), 'height', 'medium');
    return h === 'small' ? 280 : h === 'large' ? 560 : 400;
  });

  protected src = computed<SafeResourceUrl | null>(() => {
    const embed = this.embedUrl().trim();
    // Yalnız bilinen harita sağlayıcıları, yalnız https: serbest embedUrl
    // vitrinde javascript:/rastgele iframe'e (aynı origin'deki üye portalı
    // token'ını hedefleyen betiğe) dönüşebilirdi.
    if (embed) return haritaHostIzinli(embed) ? this.sanitizer.bypassSecurityTrustResourceUrl(embed) : null;
    const q = this.query().trim();
    if (!q) return null;
    const url = this.provider() === 'osm'
      ? `https://www.openstreetmap.org/export/embed.html?bbox=&layer=mapnik&marker=${encodeURIComponent(q)}`
      : `https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });
}

/** Gömülü harita için izinli host'lar; başka her şey reddedilir. */
function haritaHostIzinli(url: string): boolean {
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:') return false;
    const h = u.hostname.toLowerCase();
    const izinli = ['www.google.com', 'google.com', 'maps.google.com', 'www.openstreetmap.org', 'openstreetmap.org', 'www.bing.com', 'api.mapbox.com', 'yandex.com', 'yandex.com.tr'];
    return izinli.includes(h) || h.endsWith('.google.com');
  } catch {
    return false;
  }
}
